import { EventEmitter } from 'events';
import { gql } from 'graphql-request';
import { Disposable } from '@gitlab-org/disposable';
import { createInterfaceId, Injectable } from '@gitlab-org/di';
import { ConfigService, IConfig } from '../config_service';
import { ApiRequest } from '../api_types';
import { log } from '../log';
import { GitLabApiClient } from '../api';
import { NO_LICENSE } from './feature_state_management_types';
import { StateCheck, StateCheckChangedEventData } from './state_check';

export interface GqlDuoCodeSuggestionsAvailable {
  currentUser: {
    duoCodeSuggestionsAvailable: boolean;
  };
}

const querySuggestionsAvailable = gql`
  query suggestionsAvailable {
    currentUser {
      duoCodeSuggestionsAvailable
    }
  }
`;
export interface CodeSuggestionsDuoLicenseCheck extends StateCheck<typeof NO_LICENSE> {}

export const CodeSuggestionsDuoLicenseCheck = createInterfaceId<CodeSuggestionsDuoLicenseCheck>(
  'CodeSuggestionsDuoLicenseCheck',
);

@Injectable(CodeSuggestionsDuoLicenseCheck, [GitLabApiClient, ConfigService])
export class DefaultCodeSuggestionsDuoLicenseCheck implements StateCheck<typeof NO_LICENSE> {
  #subscriptions: Disposable[] = [];

  #stateEmitter = new EventEmitter();

  #api: GitLabApiClient;

  #isLicenseAvailable = false;

  #token?: string;

  #baseUrl?: string;

  constructor(api: GitLabApiClient, configService: ConfigService) {
    this.#api = api;

    this.#subscriptions.push(
      configService.onConfigChange(async (config) => {
        const { token, baseUrl } = config.client;
        // FIXME: When configService has `affectsConfiguration` implemented - use that instead
        // https://gitlab.com/gitlab-org/editor-extensions/gitlab-lsp/-/issues/405
        // to avoid multiple API calls when neither token or baseUrl has changed
        if (baseUrl && token && (this.#baseUrl !== baseUrl || this.#token !== token)) {
          this.#baseUrl = baseUrl;
          this.#token = token;
          await this.#checkIfLicenseAvailable(config);
        }
      }),
    );
  }

  async #checkIfLicenseAvailable(config: IConfig): Promise<void> {
    try {
      const response = await this.#api.fetchFromApi<GqlDuoCodeSuggestionsAvailable>({
        type: 'graphql',
        query: querySuggestionsAvailable,
        variables: {},
      } satisfies ApiRequest<GqlDuoCodeSuggestionsAvailable>);

      this.#isLicenseAvailable = response.currentUser.duoCodeSuggestionsAvailable;
    } catch (error) {
      log.error(
        `Failed to request information about Duo Pro license on ${config.client.baseUrl}`,
        error,
      );
      this.#isLicenseAvailable = false;
    }

    this.#stateEmitter.emit('change', this);
  }

  onChanged(listener: (data: StateCheckChangedEventData) => void): Disposable {
    this.#stateEmitter.on('change', listener);
    return {
      dispose: () => this.#stateEmitter.removeListener('change', listener),
    };
  }

  get engaged() {
    return !this.#isLicenseAvailable;
  }

  id = NO_LICENSE;

  details =
    'Code Suggestions is now a paid feature, part of Duo Pro. Contact your GitLab administrator to upgrade';

  dispose() {
    this.#subscriptions.forEach((s) => s.dispose());
  }
}
